
import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import CookieBanner from '../../components/feature/CookieBanner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    document.title = 'İletişim - İzmir Vize Danışmanlığı | LH Global Pass';
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      alert('Lütfen tüm zorunlu alanları doldurun.');
      return;
    }

    if (formData.message.length > 500) {
      alert('Mesajınız 500 karakteri geçemez.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4othufiu80n4l8qnkug', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/905422671171?text=Merhaba%20ben%20lhglobalpass.com%20%C3%BCzerinden%20ileti%C5%9Fime%20ge%C3%A7iyorum', '_blank');
  };

  const handleCall = () => {
    window.location.href = 'tel:+905422671171';
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-24 bg-gradient-to-br from-navy to-navy-light text-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl lg:text-6xl font-bold mb-6">
                İletişim
              </h1>
              <p className="text-xl text-gray-200 leading-relaxed">
                <strong>İzmir vize danışmanlığı</strong> için bizimle iletişime geçin
              </p>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div>
                <h2 className="text-3xl font-bold text-navy mb-6">Bize Ulaşın</h2>
                <p className="text-gray-600 mb-8">
                  Vize başvurunuz veya danışmanlık hizmetlerimiz hakkında bilgi almak için formu doldurun, size en kısa sürede dönüş yapalım.
                </p>
                <form onSubmit={handleSubmit} id="contact-form" data-readdy-form className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-navy mb-2">
                      Ad Soyad <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold text-sm"
                      placeholder="Adınız ve soyadınız"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-navy mb-2">
                      E-posta <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold text-sm"
                      placeholder="ornek@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-navy mb-2">
                      Telefon <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold text-sm"
                      placeholder="+90 5XX XXX XX XX"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-navy mb-2">
                      Konu
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold text-sm"
                    >
                      <option value="">Konu seçin</option>
                      <option value="Schengen Vizesi">Schengen Vizesi</option>
                      <option value="Amerika Vizesi">Amerika Vizesi</option>
                      <option value="İngiltere Vizesi">İngiltere Vizesi</option>
                      <option value="Oturum Kartı">Oturum Kartı</option>
                      <option value="Göçmenlik">Göçmenlik</option>
                      <option value="E-Vize">E-Vize</option>
                      <option value="Diğer">Diğer</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-navy mb-2">
                      Mesajınız <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold text-sm resize-none"
                      placeholder="Mesajınızı buraya yazın (maksimum 500 karakter)"
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 karakter</p>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gold hover:bg-gold-dark text-navy px-8 py-4 rounded-full text-base font-semibold transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? 'Gönderiliyor...' : 'Mesaj Gönder'}
                  </button>
                  {submitStatus === 'success' && (
                    <p className="text-green-600 text-sm text-center">Mesajınız başarıyla gönderildi!</p>
                  )}
                  {submitStatus === 'error' && (
                    <p className="text-red-600 text-sm text-center">Bir hata oluştu. Lütfen tekrar deneyin.</p>
                  )}
                </form>
              </div>

              {/* Contact Info */}
              <div>
                <h2 className="text-3xl font-bold text-navy mb-6">İletişim Bilgileri</h2>
                <div className="space-y-6 mb-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-full flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl text-gold"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-navy mb-1">Adres</h4>
                      <p className="text-gray-600 text-sm">
                        Fevzi Paşa Mah. 847/1 Sk. No:8 Kat:4 Daire:4/405
                        <br />
                        Sağlık İş Hanı, Konak/İzmir
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-full flex-shrink-0">
                      <i className="ri-phone-line text-2xl text-gold"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-navy mb-1">Telefon</h4>
                      <a href="tel:+905422671171" className="text-gray-600 text-sm hover:text-gold transition-colors">
                        +90 542 267 11 71
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-full flex-shrink-0">
                      <i className="ri-mail-line text-2xl text-gold"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-navy mb-1">E-posta</h4>
                      <a href="mailto:lhglobalpass@gmail.com" className="text-gray-600 text-sm hover:text-gold transition-colors">
                        lhglobalpass@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-full flex-shrink-0">
                      <i className="ri-time-line text-2xl text-gold"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-navy mb-1">Çalışma Saatleri</h4>
                      <p className="text-gray-600 text-sm">
                        Pazartesi - Cuma: 09:00 - 18:00
                        <br />
                        Cumartesi - Pazar: Kapalı
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quick Contact Buttons */}
                <div className="space-y-4">
                  <button
                    onClick={handleWhatsApp}
                    className="w-full bg-gold hover:bg-gold-dark text-navy px-8 py-4 rounded-full text-base font-semibold transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line text-xl"></i>
                    WhatsApp ile İletişim
                  </button>
                  <button
                    onClick={handleCall}
                    className="w-full bg-navy hover:bg-navy-light text-white px-8 py-4 rounded-full text-base font-semibold transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-phone-line text-xl"></i>
                    Hemen Ara
                  </button>
                </div>

                {/* Map */}
                <div className="mt-8 rounded-2xl overflow-hidden shadow-lg h-[300px]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3125.4892847392847!2d27.126487!3d38.419200!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14bbd862a0000000%3A0x0!2zMzjCsDI1JzA5LjEiTiAyN8KwMDcnMzUuNCJF!5e0!3m2!1str!2str!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="LH Global Pass Ofis Konumu"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <CookieBanner />
    </div>
  );
};

export default ContactPage;
